// Şubeler — depo/kullanıcı şube atamaları için merkezi tanım sayfası
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import { TabIcon } from '../utils/tabIcons';
import { Button, Modal, FormField, EmptyState, ListPageHeader, FilterBar } from '../components/ui';
import { CardHero, CardStatusBadge, CardTabs, CardSplitButton } from '../components/card';

const emptyBranch = {
  code: '', name: '', address: '', city: '', district: '',
  phone: '', email: '', manager_personnel_id: '', notes: '', is_active: true, is_dealer: false,
  // E-fatura override
  pos_einvoice_mode: 'auto', einvoice_prefix: '', eirsaliye_enabled: false,
  // Bayilik VKN
  legal_name: '', tax_number: '', tax_office: '',
  // Hızlı Bilişim override
  hb_override_enabled: false,
  hb_base_url: '', hb_username: '', hb_password: '', hb_secret_key: '',
};

const emptyTerminal = {
  branch_id: '', code: '', name: '',
  brand: 'protel', base_url: '', username: '', password: '',
  terminal_id: '', rvc_id: '1', polling_max_sec: 300,
  computer_name: '', notes: '', is_active: true,
};

export default function BranchesPage() {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ ...emptyBranch });
  const [isEditing, setIsEditing] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [terminals, setTerminals] = useState([]);
  const [termModal, setTermModal] = useState(null); // null | 'new' | terminal-id
  const [termForm, setTermForm] = useState({ ...emptyTerminal });
  const [termSaving, setTermSaving] = useState(false);
  const [personnel, setPersonnel] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [filters, setFilters] = useState({ is_active: '' });
  const [fetchError, setFetchError] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true); setFetchError(null);
    try {
      const params = { search, _t: Date.now() };  // cache bust
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/branches', { params });
      if (res.data.success) {
        const items = res.data.data?.items || [];
        setList(items);
        setTotal(res.data.data?.total ?? items.length);
      } else {
        setFetchError(res.data?.message || 'Liste alınamadı');
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.message;
      console.error('Şube listesi alınamadı:', status, msg);
      setFetchError(status === 403
        ? 'Bu sayfaya yetkiniz yok (branches.view izni gerekiyor). Roller ve İzinler ekranından rolünüze ekleyin.'
        : `API hatası (${status || 'bilinmiyor'}): ${msg}`);
      setList([]);
    } finally { setLoading(false); }
  }, [search, filters]);

  const fetchPersonnel = async () => {
    try {
      const res = await api.get('/personnel', { params: { limit: 200 } });
      if (res.data.success) setPersonnel(res.data.data.items || res.data.data || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchPersonnel(); }, []);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const fetchTerminals = async (branchId) => {
    if (!branchId) { setTerminals([]); return; }
    try {
      const r = await api.get('/pos-terminals', { params: { branch_id: branchId } });
      if (r.data.success) setTerminals(r.data.data?.items || []);
    } catch (e) { console.error(e); setTerminals([]); }
  };

  const handleSelect = async (b) => {
    try {
      const res = await api.get('/branches/' + b.id);
      if (res.data.success) {
        setSelected(res.data.data); setForm(res.data.data);
        setIsEditing(false); setIsNew(false); setActiveTab('general'); setMsg(null);
        await fetchTerminals(res.data.data.id);
        setShowModal(true);
      }
    } catch (err) { console.error(err); }
  };

  const handleNew = () => {
    setSelected(null); setForm({ ...emptyBranch }); setIsEditing(true); setIsNew(true);
    setActiveTab('general'); setMsg(null); setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false); setSelected(null); setForm({ ...emptyBranch });
    setIsEditing(false); setIsNew(false); setMsg(null);
  };

  const handleEdit = () => { setIsEditing(true); setMsg(null); };
  const handleCancel = () => {
    if (isNew) handleModalClose();
    else { setForm(selected); setIsEditing(false); setMsg(null); }
  };

  const handleSave = async () => {
    if (!form.code || !form.name) { setMsg({ type: 'error', text: 'Şube kodu ve adı zorunlu' }); return; }
    setSaving(true); setMsg(null);
    try {
      if (isNew) {
        const r = await api.post('/branches', form);
        if (r.data.success) {
          setMsg({ type: 'success', text: 'Şube oluşturuldu' });
          await fetchList();
          const id = r.data.data?.id;
          if (id) {
            const det = await api.get('/branches/' + id);
            if (det.data.success) { setSelected(det.data.data); setForm(det.data.data); }
          }
          setIsNew(false); setIsEditing(false);
        }
      } else {
        await api.put('/branches/' + selected.id, form);
        setMsg({ type: 'success', text: 'Şube güncellendi' });
        await fetchList();
        const det = await api.get('/branches/' + selected.id);
        if (det.data.success) { setSelected(det.data.data); setForm(det.data.data); }
        setIsEditing(false);
      }
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Kayıt başarısız' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm(`"${selected.name}" şubesini silmek istediğinize emin misiniz?`)) return;
    try {
      const r = await api.delete('/branches/' + selected.id);
      if (r.data.success) { handleModalClose(); fetchList(); }
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Silme başarısız' });
    }
  };

  const columns = [
    { id: 'code', header: 'Şube Kodu', accessor: 'code', sortable: true, width: 130,
      cell: b => <span className="stock-code">{b.code}</span> },
    { id: 'name', header: 'Şube Adı', accessor: 'name', sortable: true,
      cell: b => <span className="stock-name-cell">{b.name}</span> },
    { id: 'city', header: 'Şehir', accessor: 'city', sortable: true, width: 140,
      cell: b => <span className="stock-secondary">{b.city || '-'}</span> },
    { id: 'phone', header: 'Telefon', accessor: 'phone', width: 140,
      cell: b => <span className="stock-secondary">{b.phone || '-'}</span> },
    { id: 'manager', header: 'Yönetici', accessor: 'manager_name', sortable: true, width: 200,
      cell: b => <span className="stock-secondary">{b.manager_name || '-'}</span> },
    { id: 'warehouse_count', header: 'Depo', accessor: 'warehouse_count', sortable: true, width: 80,
      headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
      cell: b => <span className="stock-secondary">{b.warehouse_count ?? 0}</span> },
    { id: 'user_count', header: 'Kullanıcı', accessor: 'user_count', sortable: true, width: 90,
      headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
      cell: b => <span className="stock-secondary">{b.user_count ?? 0}</span> },
    { id: 'status', header: 'Durum', accessor: b => b.is_active ? 'Aktif' : 'Pasif', sortable: true, width: 90,
      headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
      cell: b => <span className={'stock-badge ' + (b.is_active ? 'active' : 'passive')}>{b.is_active ? 'Aktif' : 'Pasif'}</span> },
  ];

  const tabs = [
    { key: 'general', label: 'Genel', icon: 'home' },
    { key: 'address', label: 'Adres / İletişim', icon: 'map-pin' },
    { key: 'pos',     label: 'Kasa Ayarları', icon: 'shopping-cart' },
    { key: 'einvoice', label: 'E-Fatura / Bayilik', icon: 'file-text' },
    { key: 'notes',   label: 'Notlar', icon: 'file-text' },
  ];

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Şubeler"
        count={total}
        unit="şube"
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Kod, ad veya şehir ara..."
        onRefresh={fetchList}
        refreshing={loading}
        actions={<Button size="sm" onClick={handleNew}>+ Yeni Şube</Button>}
      />

      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'is_active', type: 'select', label: 'Durum',
            options: [{ value: '', label: 'Tümü' }, { value: 'yes', label: 'Aktif' }, { value: 'no', label: 'Pasif' }] },
        ]}
      />

      {fetchError && (
        <div style={{ padding: '10px 14px', borderRadius: 8, margin: '10px 0',
          background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.35)',
          color: '#b91c1c', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 700 }}>⚠ Liste yüklenemedi:</span>
          <span style={{ flex: 1 }}>{fetchError}</span>
          <Button size="sm" variant="ghost" onClick={fetchList}>Tekrar dene</Button>
        </div>
      )}

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="branches-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Şube bulunamadı"
          data={list}
          rowKey="id"
          onRowClick={b => handleSelect(b)}
          columns={columns}
        />
      </div>

      {showModal && (
        <Modal
          open={showModal}
          onClose={handleModalClose}
          title={isNew ? 'Yeni Şube' : (selected?.name || 'Şube')}
          width={780}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {!isNew && !isEditing && (
                  <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>
                )}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8 }}>
                {isEditing ? (
                  <>
                    <Button variant="ghost" onClick={handleCancel}>Vazgeç</Button>
                    <Button onClick={handleSave} disabled={saving}>{saving ? 'Kaydediliyor...' : '💾 Kaydet'}</Button>
                  </>
                ) : (
                  !isNew && <Button onClick={handleEdit}>✎ Düzenle</Button>
                )}
              </div>
            </>
          }>
          <CardHero
            title={form.name || 'Yeni Şube'}
            subtitle={form.code ? 'Kod: ' + form.code : ''}
          />
          {msg && (
            <div style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 10,
              background: msg.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
              color: msg.type === 'success' ? '#16a34a' : '#dc2626', fontSize: 13 }}>
              {msg.text}
            </div>
          )}

          <CardTabs
            items={tabs.map(t => ({ key: t.key, label: t.label, icon: <TabIcon name={t.icon} /> }))}
            active={activeTab} onChange={setActiveTab}
          />

          {activeTab === 'general' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
              <FormField label="Şube Kodu" required>
                <input value={form.code || ''} onChange={e => f('code', e.target.value)} disabled={!isEditing} />
              </FormField>
              <FormField label="Şube Adı" required>
                <input value={form.name || ''} onChange={e => f('name', e.target.value)} disabled={!isEditing} />
              </FormField>
              <FormField label="Şube Yöneticisi (Personel)">
                <select value={form.manager_personnel_id || ''} onChange={e => f('manager_personnel_id', e.target.value || null)} disabled={!isEditing}>
                  <option value="">— Seçiniz —</option>
                  {personnel.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                </select>
              </FormField>
              <FormField label="Durum">
                <select value={form.is_active ? '1' : '0'} onChange={e => f('is_active', e.target.value === '1')} disabled={!isEditing}>
                  <option value="1">Aktif</option>
                  <option value="0">Pasif</option>
                </select>
              </FormField>
              <FormField label="Şube Tipi" hint="Kendi şubeniz mi yoksa bayi mi?">
                <select value={form.is_dealer ? '1' : '0'} onChange={e => f('is_dealer', e.target.value === '1')} disabled={!isEditing}>
                  <option value="0">Kendi Şubem</option>
                  <option value="1">Bayi</option>
                </select>
              </FormField>
            </div>
          )}

          {activeTab === 'address' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
              <FormField label="Telefon">
                <input value={form.phone || ''} onChange={e => f('phone', e.target.value)} disabled={!isEditing} />
              </FormField>
              <FormField label="E-posta">
                <input type="email" value={form.email || ''} onChange={e => f('email', e.target.value)} disabled={!isEditing} />
              </FormField>
              <FormField label="Şehir">
                <input value={form.city || ''} onChange={e => f('city', e.target.value)} disabled={!isEditing} />
              </FormField>
              <FormField label="İlçe">
                <input value={form.district || ''} onChange={e => f('district', e.target.value)} disabled={!isEditing} />
              </FormField>
              <FormField label="Adres" style={{ gridColumn: '1/-1' }}>
                <textarea rows={3} value={form.address || ''} onChange={e => f('address', e.target.value)} disabled={!isEditing} />
              </FormField>
            </div>
          )}

          {activeTab === 'pos' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{
                padding: 10, borderRadius: 8, fontSize: 12,
                background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.25)',
                color: 'var(--text-secondary)',
              }}>
                💳 Bu şubede çalışan yazar kasalar (PC + OKC çiftleri). Bir şubede tek GİB hesabı olur ama N adet kasa olabilir.
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: 13 }}>📋 Şubedeki Kasalar ({terminals.length})</strong>
                {selected && (
                  <Button size="sm" onClick={() => {
                    setTermForm({ ...emptyTerminal, branch_id: selected.id });
                    setTermModal('new');
                  }}>+ Yeni Kasa</Button>
                )}
              </div>

              {!selected ? (
                <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
                  Şube önce kaydedilmeli; kayıt sonrası kasa eklenebilir.
                </div>
              ) : terminals.length === 0 ? (
                <EmptyState compact title="Bu şubede tanımlı kasa yok" description='"+ Yeni Kasa" ile başlayın.' />
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-secondary)' }}>
                      <th style={posTh}>Kod</th>
                      <th style={{ ...posTh, textAlign: 'left' }}>Ad</th>
                      <th style={posTh}>Marka</th>
                      <th style={posTh}>Terminal ID</th>
                      <th style={posTh}>Bilgisayar</th>
                      <th style={posTh}>Durum</th>
                      <th style={{ ...posTh, width: 110 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {terminals.map(t => (
                      <tr key={t.id} style={{ borderTop: '1px solid var(--border-secondary)' }}>
                        <td style={posTd}><span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{t.code}</span></td>
                        <td style={{ ...posTd, textAlign: 'left' }}>{t.name}</td>
                        <td style={posTd}>{t.brand || '—'}</td>
                        <td style={{ ...posTd, fontFamily: 'monospace', fontSize: 11 }}>{t.terminal_id || '—'}</td>
                        <td style={{ ...posTd, fontSize: 11, color: 'var(--text-tertiary)' }}>{t.computer_name || '—'}</td>
                        <td style={posTd}>
                          <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 'var(--r-full)',
                            background: t.is_active ? 'rgba(16,185,129,0.15)' : 'rgba(100,116,139,0.15)',
                            color: t.is_active ? '#10b981' : '#64748b', fontWeight: 700 }}>
                            {t.is_active ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                        <td style={posTd}>
                          <button onClick={() => { setTermForm(t); setTermModal(t.id); }}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--accent-primary)', marginRight: 4 }}>✎</button>
                          <button onClick={async () => {
                            if (!window.confirm(`"${t.name}" kasası silinsin mi?`)) return;
                            try {
                              await api.delete('/pos-terminals/' + t.id);
                              fetchTerminals(selected.id);
                            } catch (e) { alert(e.response?.data?.message || 'Silme hatası'); }
                          }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--danger)' }}>🗑</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'einvoice' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{
                padding: 10, borderRadius: 8, fontSize: 12,
                background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)',
                color: 'var(--text-secondary)',
              }}>
                ℹ Şubeye özel ayarlar — <strong>boş bırakılırsa şirket genel ayarları kullanılır.</strong>
                {form.is_dealer && <span> Bayi (kendi VKN) ise VKN bilgilerini doldurun.</span>}
              </div>

              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>📄 E-Belge Davranışı</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <FormField label="E-Belge Kaynağı" hint="Kim keser? ERP mi, kasa mı?">
                  <select value={form.pos_einvoice_mode || 'auto'} onChange={e => f('pos_einvoice_mode', e.target.value)} disabled={!isEditing}>
                    <option value="auto">Otomatik (Mükellefse e-Fatura, değilse e-Arşiv)</option>
                    <option value="efatura">Zorla e-Fatura</option>
                    <option value="earsiv">Zorla e-Arşiv</option>
                    <option value="fis_only">Sadece Fiş (e-belge kesme)</option>
                  </select>
                </FormField>
                <FormField label="Şube Özel Prefix" hint="Boş = şirket geneli prefix'i kullanır (örn: SF-MERKEZ-)">
                  <input value={form.einvoice_prefix || ''} onChange={e => f('einvoice_prefix', e.target.value)} disabled={!isEditing} placeholder="(opsiyonel)" />
                </FormField>
                <FormField label="e-İrsaliye Aktif">
                  <select value={form.eirsaliye_enabled ? '1' : '0'} onChange={e => f('eirsaliye_enabled', e.target.value === '1')} disabled={!isEditing}>
                    <option value="0">Hayır (şirket geneline göre)</option>
                    <option value="1">Evet (bu şubeden e-irsaliye kesilir)</option>
                  </select>
                </FormField>
              </div>

              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)', marginTop: 8 }}>🏛 Bayilik / Kendi VKN</div>
              <div style={{ padding: 8, fontSize: 12, color: 'var(--text-tertiary)' }}>
                {form.is_dealer
                  ? 'Bu şube BAYİ olarak işaretli — kendi VKN ve GİB hesabıyla faturalandırma yapılır.'
                  : 'Tek VKN senaryosu: bu alanlar boş bırakılır, şirket genel VKN bilgisi kullanılır.'}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <FormField label="Resmi Ünvan" hint="Boş = şirket ünvanı kullanılır">
                  <input value={form.legal_name || ''} onChange={e => f('legal_name', e.target.value)} disabled={!isEditing} placeholder="(opsiyonel)" />
                </FormField>
                <FormField label="VKN">
                  <input value={form.tax_number || ''} onChange={e => f('tax_number', e.target.value)} disabled={!isEditing} placeholder="1234567890" />
                </FormField>
                <FormField label="Vergi Dairesi">
                  <input value={form.tax_office || ''} onChange={e => f('tax_office', e.target.value)} disabled={!isEditing} />
                </FormField>
              </div>

              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)', marginTop: 8 }}>⚡ Hızlı Bilişim Override (Bayilikte Şubeye Özel)</div>
              <div style={{ padding: 8, fontSize: 12, color: 'var(--text-tertiary)' }}>
                Bayinin kendi Hızlı Bilişim hesabı varsa burayı aç ve credentials gir. Aksi halde şirket genel hesabı kullanılır.
              </div>
              <FormField label="Override Aktif">
                <select value={form.hb_override_enabled ? '1' : '0'} onChange={e => f('hb_override_enabled', e.target.value === '1')} disabled={!isEditing}>
                  <option value="0">Hayır — Şirket genelini kullan</option>
                  <option value="1">Evet — Bu şubeye özel HB hesabı</option>
                </select>
              </FormField>
              {form.hb_override_enabled && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                  <FormField label="HB Base URL">
                    <input value={form.hb_base_url || ''} onChange={e => f('hb_base_url', e.target.value)} disabled={!isEditing} placeholder="https://econnect..." />
                  </FormField>
                  <FormField label="HB Username">
                    <input value={form.hb_username || ''} onChange={e => f('hb_username', e.target.value)} disabled={!isEditing} />
                  </FormField>
                  <FormField label="HB Password">
                    <input type="password" value={form.hb_password || ''} onChange={e => f('hb_password', e.target.value)} disabled={!isEditing} autoComplete="new-password" />
                  </FormField>
                  <FormField label="HB Secret Key">
                    <input type="password" value={form.hb_secret_key || ''} onChange={e => f('hb_secret_key', e.target.value)} disabled={!isEditing} autoComplete="new-password" />
                  </FormField>
                </div>
              )}
            </div>
          )}

          {activeTab === 'notes' && (
            <FormField label="Notlar">
              <textarea rows={6} value={form.notes || ''} onChange={e => f('notes', e.target.value)} disabled={!isEditing} />
            </FormField>
          )}

        </Modal>
      )}

      {/* Kasa Sub-Modal */}
      {termModal && (
        <Modal
          open={true}
          onClose={() => { setTermModal(null); setTermForm({ ...emptyTerminal }); }}
          title={termModal === 'new' ? 'Yeni Kasa' : 'Kasa Düzenle'}
          width={680}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {termModal !== 'new' && (
                  <Button variant="ghost" disabled={termSaving} onClick={async () => {
                    try {
                      const r = await api.post('/pos-terminals/' + termModal + '/test-connection');
                      const d = r.data?.data || {};
                      if (d.success) {
                        alert(`✓ Bağlantı başarılı!\nCihaz sayısı: ${(d.devices || []).length}`);
                      } else {
                        alert(`✗ Bağlantı başarısız:\n${d.error || 'bilinmeyen hata'}`);
                      }
                    } catch (e) {
                      alert('✗ ' + (e.response?.data?.message || e.message));
                    }
                  }}>🔌 Bağlantıyı Test Et</Button>
                )}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8 }}>
                <Button variant="ghost" onClick={() => { setTermModal(null); setTermForm({ ...emptyTerminal }); }}>İptal</Button>
                <Button disabled={termSaving} onClick={async () => {
                  if (!termForm.code || !termForm.name) { alert('Kod ve ad zorunlu'); return; }
                  setTermSaving(true);
                  try {
                    if (termModal === 'new') {
                      await api.post('/pos-terminals', termForm);
                    } else {
                      await api.put('/pos-terminals/' + termModal, termForm);
                    }
                    await fetchTerminals(selected.id);
                    setTermModal(null);
                    setTermForm({ ...emptyTerminal });
                  } catch (e) {
                    alert(e.response?.data?.message || 'Kayıt başarısız');
                  } finally { setTermSaving(false); }
                }}>{termSaving ? 'Kaydediliyor...' : '💾 Kaydet'}</Button>
              </div>
            </>
          }>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <FormField label="Kasa Kodu" required>
              <input value={termForm.code || ''} onChange={e => setTermForm(p => ({ ...p, code: e.target.value }))} placeholder="KASA-01" />
            </FormField>
            <FormField label="Kasa Adı" required>
              <input value={termForm.name || ''} onChange={e => setTermForm(p => ({ ...p, name: e.target.value }))} placeholder="Reyon Kasası" />
            </FormField>
            <FormField label="Marka">
              <select value={termForm.brand || 'protel'} onChange={e => setTermForm(p => ({ ...p, brand: e.target.value }))}>
                <option value="protel">Protel / Simpra Quick</option>
                <option value="hugin">Hugin OFD</option>
                <option value="vodafone">Vodafone YazarKasa POS</option>
                <option value="other">Diğer</option>
              </select>
            </FormField>
            <FormField label="Durum">
              <select value={termForm.is_active ? '1' : '0'} onChange={e => setTermForm(p => ({ ...p, is_active: e.target.value === '1' }))}>
                <option value="1">Aktif</option>
                <option value="0">Pasif</option>
              </select>
            </FormField>
            <FormField label="Base URL" hint="Protel test: https://gmubasket-apitest.protel.com.tr">
              <input value={termForm.base_url || ''} onChange={e => setTermForm(p => ({ ...p, base_url: e.target.value }))} />
            </FormField>
            <FormField label="Terminal ID" hint="Yazarkasa seri numarası">
              <input value={termForm.terminal_id || ''} onChange={e => setTermForm(p => ({ ...p, terminal_id: e.target.value }))} placeholder="79886fb3b50a8d1e" />
            </FormField>
            <FormField label="RvcId">
              <input value={termForm.rvc_id || ''} onChange={e => setTermForm(p => ({ ...p, rvc_id: e.target.value }))} placeholder="1" />
            </FormField>
            <FormField label="Polling Maks (sn)">
              <input type="number" min={30} max={600} value={termForm.polling_max_sec || ''} onChange={e => setTermForm(p => ({ ...p, polling_max_sec: parseInt(e.target.value) || 300 }))} />
            </FormField>
            <FormField label="Username (API)">
              <input value={termForm.username || ''} onChange={e => setTermForm(p => ({ ...p, username: e.target.value }))} />
            </FormField>
            <FormField label="Password (API)">
              <input type="password" autoComplete="new-password" value={termForm.password || ''} onChange={e => setTermForm(p => ({ ...p, password: e.target.value }))} />
            </FormField>
            <FormField label="Bilgisayar Adı (opsiyonel)" hint="Hangi PC'ye bağlı (debug için)">
              <input value={termForm.computer_name || ''} onChange={e => setTermForm(p => ({ ...p, computer_name: e.target.value }))} placeholder="MAGAZA-PC-01" />
            </FormField>
            <FormField label="Notlar" fullWidth style={{ gridColumn: '1/-1' }}>
              <textarea rows={2} value={termForm.notes || ''} onChange={e => setTermForm(p => ({ ...p, notes: e.target.value }))} />
            </FormField>
          </div>
        </Modal>
      )}
    </div>
  );
}

const posTh = { padding: '8px 10px', fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.4, textAlign: 'center' };
const posTd = { padding: '8px 10px', textAlign: 'center' };
